<template>
  <v-dialog
    v-model="internalVisible"
    max-width="520"
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card>
      <!-- Card Title -->
      <v-card-title class="headline">
        {{ title }}
      </v-card-title>
      <!-- Card Content -->
      <v-card-text>
        <div class="d-flex align-center mb-6">
          <img
            width="60"
            :src="photoUrls.length ? $convertImgix(`${photoUrls[0]}?w=128`) : noPicture"
            alt="product"
            class="mr-4 rounded"
          />
          <div>
            <p class="font-weight-medium mb-1">
              {{ name }}
            </p>
            <p class="text-body-2 mb-1">
              {{ code }}
            </p>
            <p class="text-body-2 mb-1">
              {{ colorSize }}
            </p>
            <p class="text-body-2 mb-1">
              COLLECTION : {{ collection.toUpperCase() }}
            </p>
            <p class="text-body-2 mb-1">
              LOCATION : {{ location.name }}
            </p>
            <p class="text-body-2 mb-1">
              จำนวนปัจจุบัน {{ qty | showNumberFormat() }}
            </p>
          </div>
        </div>
        <v-form
          ref="form"
          v-model="valid">
          <v-autocomplete
            v-model="destination"
            :items="items"
            :loading="loading"
            :search-input.sync="search"
            clearable
            return-object
            hide-selected
            class="mb-2"
            item-text="name"
            item-value="id"
            label="เลือก location ที่แนะนำ หรือ พิมพ์เพื่อค้นหา"
            outlined
            dense
            :rules="[v => !!v || 'กรุณาเลือก location']"
            @click:clear="fetchProductLocation(false, true)"
          >
            <template v-slot:no-data>
              <v-list-item v-if="loading">
                <v-list-item-title>
                  Loading...
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-else>
                <v-list-item-title>
                  ไม่พบข้อมูล location
                </v-list-item-title>
              </v-list-item>
            </template>

            <template v-slot:append-item>
              <div
                v-intersect="onScroll"
                class="text-center py-2">
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  size="24"
                  color="primary"></v-progress-circular>
                <span v-else-if="!allItemsLoaded && items.length">Scroll เพื่อโหลดข้อมูลเพิ่ม...</span>
                <span
                  v-else-if="items.length && listState === 'recommend'"
                  class="text-caption">Location ที่แนะนำหมดแล้วกรุณาพิมพ์เพื่อค้นหา location อื่นๆ</span>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title v-text="item.name"></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.showQty">
                {{ item.qty | showNumberFormat }} PCS.
              </v-list-item-action>
            </template>
          </v-autocomplete>
          <v-text-field
            v-model="inputValue"
            label="Enter number"
            :max="qty"
            :min="0"
            type="number"
            outlined
            dense
            :rules="[
              v => v !== null && v !== '' || 'กรุณากรอกจำนวนสินค้าที่จะย้าย',
              v => v > 0 || 'จำนวนต้องมากกว่า 0',
              v => v <= qty || `จำนวนสินค้าที่จะย้ายเกินกว่าจำนวนคงเหลือ`
            ]"
            @keypress="onlyNumber"
          />
        </v-form>
      </v-card-text>

      <v-divider />

      <!-- Card Actions -->
      <v-card-actions class="justify-end">
        <v-btn
          text
          color="grey"
          @click="cancel"
        >
          ยกเลิก
        </v-btn>
        <v-btn
          color="secondary"
          @click="validateForm"
        >
          บันทึก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import NoPicture from '@/assets/image/no_picture_available.png'
import StockLocationProvider from '@/resources/StockLocation.provider'

const stockLocationService = new StockLocationProvider()
export default {
  name: 'MoveLocationDialog',
  components: {
  },
  props: {
    adjustmentData: {
      type: Object,
      default: () => {}
    },
    warehouse: {
      type: [String, Number],
      default: ''
    },
    location: {
      type: Object,
      default: () => {}
    },
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Enter Number'
    }
  },
  data () {
    return {
      noPicture: NoPicture,
      inputValue: this.adjustmentData.qty,
      internalVisible: this.visible,
      loading: false,
      items: [],
      search: null,
      totalPages: 0,
      totalItems: 0,
      query: {
        warehouse: null,
        page: 1,
        limit: 20
      },
      destination: null,
      qtyError: false,
      delaySearch: null,
      listState: 'recommend',
      valid: null,
      hasRecommend: false
    }
  },
  computed: {
    photoUrls () {
      return this.adjustmentData.product.photoUrls || []
    },
    name () {
      return this.adjustmentData.name || []
    },
    code () {
      return this.adjustmentData.code || []
    },
    collection () {
      return this.adjustmentData.collection || 'collection: -'
    },
    colorSize () {
      return `${this.adjustmentData.sku.color.name} - ${this.adjustmentData.sku.size.name}`.toUpperCase()
    },
    qty () {
      return this.adjustmentData.qty
    },
    totalDestinationQty () {
      return this.destinations.reduce((sum, d) => sum + ((+d.qty) || 0), 0)
    },
    allItemsLoaded () {
      return this.query.page === this.totalPages
    }
  },
  watch: {
    adjustmentData (val) {
      this.inputValue = val.qty
    },
    visible (val) {
      this.internalVisible = val
    },
    internalVisible (val) {
      this.$emit('update:visible', val)
    },
    search (val) {
      if (this.destination?.name !== val && val != null) {
        clearTimeout(this.delaySearch)
          this.delaySearch = setTimeout(() => {
            this.fetchPaginate(false, true)
        }, 300)
      }
    }
  },
  mounted () {
    this.fetchProductLocation()
  },
  methods: {
    onlyNumber (e) {
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault()
      }
    },
    cancel () {
      this.$emit('cancel')
      this.internalVisible = false
    },
    async fetchPaginate (append = false, isClear = false) {
      if (this.loading) return
      this.listState = 'location'
      if (isClear) {
        this.query.page = 1
        this.totalPages = 0
        this.totalItems = 0
      }
      try {
        this.loading = true
        const { data } = await stockLocationService.getLocationPaginate({
          ...this.query,
          warehouse: this.warehouse,
          search: this.search
        })

        this.totalPages = data.pages
        this.totalItems = data.total

        const newItems = data.results.filter((r) => r.id !== this.location.id)
        if (append) {
          this.items = [...this.items, ...newItems]
        } else {
          this.items = newItems
        }
      } catch (error) {
        console.error('getLocation', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async fetchProductLocation (append = false, isClear = false) {
      if (!this.warehouse) {
        return
      }
      this.listState = 'recommend'

      if (isClear) {
        this.query.page = 1
        this.totalPages = 0
        this.totalItems = 0
      }

      try {
        this.loading = true

        const { data } = await stockLocationService.getMappingLocationPaginate({ ...this.query, warehouse: `${this.warehouse}`, search: this.code })
        const newItem = data.results.map((r) => ({ qty: r.qty, ...r.location, showQty: true })).filter((r) => r.id !== this.location.id)
        if (newItem?.length) {
          this.hasRecommend = true
          this.totalPages = data.pages
          this.totalItems = data.total
          if (append) {
            this.items = [...this.items, ...newItem]
          } else {
            this.items = newItem
          }
        } else {
          this.loading = false
          this.fetchPaginate(false, true)
        }
      } catch (error) {
        console.error('getLocation', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    addToDestination (location) {
      this.destinations.push({
        location,
        qty: 0
      })
    },
    async validateForm () {
      const { form } = this.$refs
      if (form.validate()) {
        this.$emit('confirm', {
          productSkus: [
            {
              code: this.code,
              qty: Number(this.inputValue)
            }
          ],
          origin: this.location.id,
          destination: this.destination.id
        })
        this.internalVisible = false
      }
    },
    async onScroll (entries) {
      if (this.loading) return

      if (entries[0].isIntersecting && !this.allItemsLoaded) {
        this.query.page++
        if (this.listState === 'recommend') {
          await this.fetchProductLocation(true, false)
        } else {
          await this.fetchPaginate(true, false)
        }
      }
    }
  }
}
</script>
